import { useCallback, useState } from 'react'

export type TableSort = { orderBy: string; sortOrder: string }

/** localStorage keys used to persist per-table sort preferences. */
export const SITE_SORT_KEY = 'site-sort'
export const PROJECT_CONTENT_SORT_KEY = 'project-content-sort'

/**
 * The only `sortBy` values `metadata/project/offset-list` accepts. Sorting the
 * site table by any other column makes the request fail with "One or more
 * validation errors occurred."
 */
export const SITE_SORTABLE_FIELDS = ['name', 'createdAt', 'updatedAt']

/**
 * The complement of `SITE_SORTABLE_FIELDS` over the site table's columns, for
 * `DataTable`'s `notSortableColumns`. Keep the two lists in sync.
 */
export const SITE_UNSORTABLE_FIELDS = [
  'description',
  'datasetType',
  'datasetFormat',
  'storageSize'
]

/**
 * Reads a persisted sort preference from localStorage, falling back to
 * `defaultSort` when nothing valid is stored. Exported separately so it can be
 * reused outside of React (e.g. to hydrate a reducer's initial state).
 */
export function readSavedSort(
  tableKey: string,
  defaultSort: TableSort,
  allowedOrderBy?: string[]
): TableSort {
  try {
    const raw = localStorage.getItem(tableKey)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && parsed.orderBy && parsed.sortOrder) {
        // A column that was sortable when the preference was written may not be
        // sortable now, and replaying it only produces a failing request. Fall
        // back to the default instead of trusting whatever is in storage.
        if (!allowedOrderBy || allowedOrderBy.includes(parsed.orderBy)) {
          return { orderBy: parsed.orderBy, sortOrder: parsed.sortOrder }
        }
      }
    }
  } catch {
    // ignore malformed values
  }
  return defaultSort
}

/**
 * Persists a table's sort preference in localStorage under `tableKey`.
 *
 * Mirrors `useTablePageParam`: the hook owns state + persistence only, so it
 * works for both client-side tables (that render the returned sort directly)
 * and server-side tables (that also dispatch a reload on change).
 */
export function useTableSortParam(
  tableKey: string,
  defaultSort: TableSort,
  allowedOrderBy?: string[]
): [TableSort, (orderBy: string, sortOrder: string) => void] {
  const [sort, setSort] = useState<TableSort>(() =>
    readSavedSort(tableKey, defaultSort, allowedOrderBy)
  )

  const persistSort = useCallback(
    (orderBy: string, sortOrder: string) => {
      const next = { orderBy, sortOrder }
      try {
        localStorage.setItem(tableKey, JSON.stringify(next))
      } catch {
        // ignore write failures (e.g. private mode / quota)
      }
      setSort(next)
    },
    [tableKey]
  )

  return [sort, persistSort]
}
